import Link from "next/link"
import { AREAS_EN_ORDEN } from "@/lib/landing/catalogo"
import { modulosDe, nombreDeArea, sectoresDe } from "@/lib/landing/contenido"
import { ruta, type Idioma } from "@/lib/landing/idioma"
import { areaDe } from "@/lib/landing/precio"
import { SoloConMercado } from "@/components/mercado/mercado-visible"
import { EnlaceDeIdioma } from "./idioma-elegido"

type Columna = {
  titulo: string
  enlaces: { href: string; texto: string }[]
  /** La del Mercado: solo con el Mercado abierto (lib/mercado/visible.ts). */
  soloConMercado?: boolean
}

function columnas(idioma: Idioma): Columna[] {
  const sectores = sectoresDe(idioma).map((p) => ({ href: ruta(idioma, "sectores", p.slug), texto: p.nombre }))
  if (idioma === "en")
    return [
      {
        titulo: "Product",
        enlaces: [
          { href: "/en/modules", texto: "All modules" },
          { href: "/en/pricing", texto: "Pricing" },
          { href: "/en/work", texto: "Zindra Work" },
          { href: "/en/integrations", texto: "Integrations" },
          { href: "/en/team-app", texto: "Team app" },
        ],
      },
      { titulo: "Industries", enlaces: sectores },
      {
        titulo: "Resources",
        enlaces: [
          { href: "/en/digital-menu", texto: "Free digital menu" },
          { href: "/en/restaurants", texto: "Restaurant guide" },
          { href: "/en/templates", texto: "Templates" },
          { href: "/en/calculators", texto: "Calculators" },
          { href: "/en/glossary", texto: "Glossary" },
          { href: "/en/best", texto: "Comparisons" },
        ],
      },
      {
        soloConMercado: true,
        titulo: "Market",
        enlaces: [
          { href: "/en/market", texto: "Specialty coffee" },
          { href: "/en/distributors", texto: "Coffee distributors" },
        ],
      },
      {
        titulo: "Company",
        enlaces: [
          { href: "/en/about", texto: "About us" },
          { href: "/en/contact", texto: "Contact" },
          { href: "/en/legal/privacy", texto: "Privacy" },
          { href: "/en/legal/terms", texto: "Terms" },
          { href: "/en/legal/cookies", texto: "Cookies" },
          { href: "/", texto: "Español" },
        ],
      },
    ]
  return [
    {
      titulo: "Producto",
      enlaces: [
        { href: "/modulos", texto: "Todos los módulos" },
        { href: "/precios", texto: "Precios" },
        { href: "/work", texto: "Zindra Work" },
        { href: "/integraciones", texto: "Integraciones" },
        { href: "/empleados", texto: "App para el equipo" },
      ],
    },
    { titulo: "Sectores", enlaces: sectores },
    {
      titulo: "Recursos",
      enlaces: [
        { href: "/carta-digital", texto: "Carta digital gratis" },
        { href: "/restaurantes", texto: "Guía de restaurantes" },
        { href: "/plantillas", texto: "Plantillas" },
        { href: "/calculadora", texto: "Calculadoras" },
        { href: "/glosario", texto: "Glosario" },
        { href: "/mejores", texto: "Comparativas" },
      ],
    },
    {
      soloConMercado: true,
      titulo: "Mercado",
      enlaces: [
        { href: "/mercado", texto: "Café de especialidad" },
        { href: "/distribuidores", texto: "Distribuidores de café" },
      ],
    },
    {
      titulo: "Empresa",
      enlaces: [
        { href: "/sobre-nosotros", texto: "Sobre nosotros" },
        { href: "/contacto", texto: "Contacto" },
        { href: "/affiliate-register", texto: "Afiliados" },
        { href: "/legal/privacidad", texto: "Privacidad" },
        { href: "/legal/terminos", texto: "Términos" },
        { href: "/legal/cookies", texto: "Cookies" },
        { href: "/en", texto: "English" },
      ],
    },
  ]
}

export function Pie({ idioma = "es" }: { idioma?: Idioma }) {
  const modulos = modulosDe(idioma)
  return (
    <footer className="border-t border-zinc-200 px-6 pt-14 pb-12">
      {/* auto-fit: con o sin la columna del Mercado, las que haya se reparten el ancho. */}
      <div className="mx-auto grid max-w-[1240px] gap-10 md:grid-cols-[1.4fr_repeat(auto-fit,minmax(0,1fr))]">
        <div className="flex flex-col gap-3">
          <img src="/zindra_black.svg" alt="Zindra" className="h-6 w-auto self-start" />
          <p className="max-w-xs text-sm leading-relaxed text-zinc-600">
            {idioma === "en"
              ? "Your business, module by module: turn on what you need and pay for what you use."
              : "Tu empresa, módulo a módulo: activa lo que necesitas y paga por lo que usas."}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-zinc-500">
            {AREAS_EN_ORDEN.map((a) => (
              <span key={a}>
                {nombreDeArea(idioma, a)} · {modulos.filter((m) => areaDe(m.clave) === a).length}
              </span>
            ))}
          </div>
        </div>
        {columnas(idioma).map((c) => {
          const columna = (
            <div key={c.titulo} className="flex flex-col gap-2 text-sm">
              <span className="text-[13px] font-semibold text-[#0b0b0c]">{c.titulo}</span>
              {c.enlaces.map((e) =>
                e.texto === "English" || e.texto === "Español" ? (
                  <EnlaceDeIdioma key={e.href} href={e.href} a={e.texto === "English" ? "en" : "es"} className="text-zinc-600 hover:text-lime-700">
                    {e.texto}
                  </EnlaceDeIdioma>
                ) : (
                  <Link key={e.href} href={e.href} className="text-zinc-600 hover:text-lime-700">
                    {e.texto}
                  </Link>
                ),
              )}
            </div>
          )
          return c.soloConMercado ? <SoloConMercado key={c.titulo}>{columna}</SoloConMercado> : columna
        })}
      </div>
    </footer>
  )
}
